import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../../components/Footer';
import Header from '../../../components/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import { lookupContract, confirmMvp, type PublicContract } from '../contract/contractApi';
import './MvpConfirm.scss';

interface LookupFormState {
  customerName: string;
  phone: string;
}

type LookupErrors = Partial<Record<keyof LookupFormState, string>>;

const PHONE_PATTERN = /^[0-9+\-()\s]{8,20}$/;

const INITIAL_LOOKUP: LookupFormState = {
  customerName: '',
  phone: '',
};

const MvpConfirm: React.FC = () => {
  const navigate = useNavigate();
  const [lookup, setLookup] = useState<LookupFormState>(INITIAL_LOOKUP);
  const [lookupErrors, setLookupErrors] = useState<LookupErrors>({});
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [contract, setContract] = useState<PublicContract | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  useEffect(() => {
    const nodes = document.querySelectorAll('.mvp-confirm-page .reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const updateLookup = <K extends keyof LookupFormState>(key: K, value: LookupFormState[K]) => {
    setLookup((prev) => ({ ...prev, [key]: value }));
    setLookupErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateLookup = (): LookupErrors => {
    const nextErrors: LookupErrors = {};
    if (!lookup.customerName.trim()) nextErrors.customerName = '고객명을 입력해 주세요.';
    if (!lookup.phone.trim()) {
      nextErrors.phone = '전화번호를 입력해 주세요.';
    } else if (!PHONE_PATTERN.test(lookup.phone.trim())) {
      nextErrors.phone = '올바른 연락처 형식을 입력해 주세요.';
    }
    return nextErrors;
  };

  const handleLookup = async (event: FormEvent) => {
    event.preventDefault();
    const validationErrors = validateLookup();
    if (Object.keys(validationErrors).length > 0) {
      setLookupErrors(validationErrors);
      return;
    }

    setIsLookingUp(true);
    setLookupErrors({});
    setConfirmError('');
    try {
      const found = await lookupContract(lookup.customerName.trim(), lookup.phone.trim());
      setContract(found);
    } catch (error) {
      setContract(null);
      setLookupErrors({
        customerName: error instanceof Error ? error.message : '신청 조회에 실패했습니다.',
      });
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleConfirm = async () => {
    if (!contract) return;
    setIsConfirming(true);
    setConfirmError('');
    try {
      await confirmMvp(contract.id, {
        customer_name: lookup.customerName.trim(),
        phone: lookup.phone.trim(),
      });
      navigate('/contract');
    } catch (error) {
      setConfirmError(error instanceof Error ? error.message : 'MVP 확인 처리에 실패했습니다.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="mvp-confirm-page">
      <Header />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· 서비스신청 · MVP 확인</span>
          </div>
          <div className="page-header-grid">
            <div>
              <h1 className="page-title">
                MVP 제작 현황과,
                <br />
                <span className="em">시안</span>을 확인하세요.
              </h1>
              <p className="page-sub">
                신청 시 등록한 고객명과 전화번호를 입력하면 MVP 제작 진행 상황을 확인할 수
                있습니다.
              </p>
            </div>
            <div className="page-meta">Step 3 · MVP Confirm</div>
          </div>
        </div>
      </section>

      <section className="mvp-section">
        <div className="container">
          {!contract && (
            <div className="mvp-panel">
              <h2 className="mvp-panel__title">신청 조회</h2>
              <p className="mvp-panel__desc">
                신청 시 등록한 고객명과 전화번호를 입력해 진행 상황을 불러와 주세요.
              </p>

              <form className="mvp-form" onSubmit={handleLookup} noValidate>
                <div className="mvp-form__grid">
                  <div className="mvp-field">
                    <label className="mvp-field__label" htmlFor="mvp-customer">
                      고객명 <span className="mvp-field__required">*</span>
                    </label>
                    <input
                      id="mvp-customer"
                      className={`mvp-field__input ${
                        lookupErrors.customerName ? 'mvp-field__input--error' : ''
                      }`}
                      type="text"
                      value={lookup.customerName}
                      onChange={(e) => updateLookup('customerName', e.target.value)}
                      placeholder="홍길동"
                      autoComplete="name"
                    />
                    {lookupErrors.customerName && (
                      <p className="mvp-field__error">{lookupErrors.customerName}</p>
                    )}
                  </div>

                  <div className="mvp-field">
                    <label className="mvp-field__label" htmlFor="mvp-phone">
                      전화번호 <span className="mvp-field__required">*</span>
                    </label>
                    <PhoneNumberInput
                      id="mvp-phone"
                      className={`mvp-field__input ${
                        lookupErrors.phone ? 'mvp-field__input--error' : ''
                      }`}
                      value={lookup.phone}
                      onChange={(phone) => updateLookup('phone', phone)}
                      autoComplete="tel"
                    />
                    {lookupErrors.phone && <p className="mvp-field__error">{lookupErrors.phone}</p>}
                  </div>
                </div>

                <button type="submit" className="mvp-btn mvp-btn--primary" disabled={isLookingUp}>
                  {isLookingUp ? '조회 중...' : '조회하기'}
                </button>
              </form>
            </div>
          )}

          {contract && (contract.status === 'applied' || contract.status === 'deposit_paid') && (
            <div className="mvp-panel mvp-notice" role="status">
              <h2 className="mvp-panel__title">제작 중입니다</h2>
              <p className="mvp-panel__desc">
                {contract.status === 'applied'
                  ? '아직 기본요금 선결제가 완료되지 않았습니다. 선결제를 먼저 완료해 주세요.'
                  : 'MVP를 제작 중입니다. 준비가 완료되면 등록하신 연락처로 안내드립니다.'}
              </p>
              {contract.status === 'applied' && (
                <Link to="/payment" className="mvp-btn mvp-btn--secondary">
                  기본요금 선결제하러 가기
                </Link>
              )}
            </div>
          )}

          {contract && contract.status === 'mvp_ready' && (
            <div className="mvp-panel">
              <h2 className="mvp-panel__title">MVP 시안이 준비되었습니다</h2>
              <p className="mvp-panel__desc">
                아래 시안을 확인하신 후, 이상이 없으면 확인 완료 버튼을 눌러 계약서 작성 단계로
                진행해 주세요.
              </p>

              <div className="mvp-summary-card">
                {contract.mvp_preview_url && (
                  <div className="mvp-summary-card__row">
                    <span className="mvp-summary-card__label">시안 링크</span>
                    <span className="mvp-summary-card__value">
                      <a href={contract.mvp_preview_url} target="_blank" rel="noopener noreferrer">
                        {contract.mvp_preview_url}
                      </a>
                    </span>
                  </div>
                )}
                {contract.mvp_note && (
                  <div className="mvp-summary-card__row">
                    <span className="mvp-summary-card__label">담당자 메모</span>
                    <span className="mvp-summary-card__value">{contract.mvp_note}</span>
                  </div>
                )}
              </div>

              <div className="mvp-actions">
                <button
                  type="button"
                  className="mvp-btn mvp-btn--primary"
                  onClick={handleConfirm}
                  disabled={isConfirming}
                >
                  {isConfirming ? '처리 중...' : '확인 완료, 계약서 작성으로 진행 →'}
                </button>
                {confirmError && <p className="mvp-preparing-notice">{confirmError}</p>}
              </div>
            </div>
          )}

          {contract && contract.status === 'mvp_confirmed' && (
            <div className="mvp-panel mvp-notice" role="status">
              <h2 className="mvp-panel__title">MVP 확인이 완료되었습니다</h2>
              <p className="mvp-panel__desc">계약서 작성 및 전자서명을 진행해 주세요.</p>
              <Link to="/contract" className="mvp-btn mvp-btn--secondary">
                계약서 작성하러 가기
              </Link>
            </div>
          )}

          {contract && contract.status === 'signed' && (
            <div className="mvp-panel mvp-notice" role="status">
              <h2 className="mvp-panel__title">계약 서명이 완료되었습니다</h2>
              <p className="mvp-panel__desc">최종결제를 진행해 주세요.</p>
              <Link to="/payment" className="mvp-btn mvp-btn--secondary">
                최종결제하러 가기
              </Link>
            </div>
          )}

          {contract && contract.status === 'completed' && (
            <div className="mvp-panel mvp-notice" role="status">
              <h2 className="mvp-panel__title">모든 절차가 완료되었습니다</h2>
              <p className="mvp-panel__desc">이미 최종결제까지 완료된 신청입니다.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </div>
  );
};

export default MvpConfirm;
